import { PrintMenu } from '../print-menu';
import { Type } from '@angular/core';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { ColorUtils } from '../../../utils/color-utils';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import type { SectionWithProducts } from '../section/section-with-products';
import { ProductType } from '../../enum/dto/product-type.enum';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { LocationPriceStream } from '../../enum/shared/location-price-stream';
import { PriceUtils } from '../../../utils/price-utils';
import { Fraction } from '../../enum/shared/fraction.enum';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { exists } from '../../../functions/exists';
import { WithinNameCell } from '../../enum/shared/within-name-cell';

export class BudSupplyMenu extends PrintMenu {
  public static readonly supportedDecimalsThatCanBeFractions: Fraction[] = [
    Fraction.Quarter,
    Fraction.Half,
    Fraction.ThreeQuarters
  ];

  getPageTopMargin(): string {
    return '0.5in';
  }

  getPageBottomMargin(): string {
    return '0.5in';
  }

  getPageLeftMargin(): string {
    return '0.5in';
  }

  getPageRightMargin(): string {
    return '0.5in';
  }

  getThemeClass(): string {
    return 'bud-supply-theme';
  }

  getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderTitleImageComponent;
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.FirstPage;
  }

  getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  override fixedLocationBrandPropertyIsWithin(): WithinNameCell | null {
    return WithinNameCell.TitleLine;
  }

  getProductWrapperClass(odd: boolean) {
    return odd ? 'product-row-overlay' : '';
  }

  getRowColorFromStrainType(odd: boolean, rowViewModel: SectionRowViewModel): string {
    switch (rowViewModel?.rowStrainType()) {
      case StrainType.Indica:
        return '#F4E0D8';
      case StrainType.Sativa:
        return '#C7D6BC';
      case StrainType.Hybrid:
        return '#F9E5C7';
      case StrainType.Blend:
        return '#BDD6EE';
      case StrainType.CBD:
        return '#E4DEF1';
    }
    return null;
  }

  interceptLineItemRowBackgroundColor(color: string, odd: boolean, rowViewModel: SectionRowViewModel): string {
    return exists(color) && odd ? ColorUtils.replaceAlphaInRGBA(color, 0.5) : color;
  }

  getShowProductSubtitle(): boolean {
    return false;
  }

  getSectionHeaderUnderlined(): boolean {
    return false;
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [
      FONT.BebasNeuePro.SemiBold_600,
      FONT.BebasNeuePro.Regular_400,
      FONT.MyriadPro.Regular_400,
      FONT.MyriadPro.Bold_700,
      FONT.MyriadPro.SemiBold_600
    ];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/zpu1aoy.css';
  }

  getPriceDecimal(
    priceStream: LocationPriceStream,
    locationId: number,
    s: SectionWithProducts,
    rowVM: SectionRowViewModel,
    colVm: SectionColumnViewModel
  ): string {
    const decimal = super.getPriceDecimal(priceStream, locationId, s, rowVM, colVm);
    return PriceUtils.getCertainDecimalsAsAsciiFractions(decimal, BudSupplyMenu.supportedDecimalsThatCanBeFractions);
  }

  getSecondaryPriceDecimal(
    priceStream: LocationPriceStream,
    locationId: number,
    s: SectionWithProducts,
    rowVM: SectionRowViewModel,
    colVm: SectionColumnViewModel
  ): string {
    const decimal = super.getSecondaryPriceDecimal(priceStream, locationId, s, rowVM, colVm);
    return PriceUtils.getCertainDecimalsAsAsciiFractions(decimal, BudSupplyMenu.supportedDecimalsThatCanBeFractions);
  }

  getMakeHeaderSectionImageFillEntireSection(): boolean {
    return true;
  }

  getTitleSectionBottomMargin(): string {
    return '0';
  }

  getSizeColumnProductTypes(): ProductType[] {
    return [
      ProductType.Flower,
      ProductType.Concentrate,
      ProductType.Vape,
      ProductType.Wellness,
      ProductType.Oil,
      ProductType.Beverage
    ];
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.ProductTitle, 0],
      [SectionColumnProductInfoType.Strain, 1],
      [SectionColumnProductInfoType.StrainType, 2],
      [SectionColumnProductInfoType.Brand, 3],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(4),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(5),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(6),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(7),
      [SectionColumnProductInfoType.QuantityAndSize, 8],
      [SectionColumnProductInfoType.Size, 9],
      [SectionColumnProductInfoType.Quantity, 10],
      [SectionColumnProductInfoType.VariantType, 11],
      [SectionColumnProductInfoType.Stock, 12],
      [SectionColumnProductInfoType.GradingResults, 13],
      [SectionColumnProductInfoType.VariantPrice, 14],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 15],
      [SectionColumnProductInfoType.Badge, 16]
    ]);
  }

  getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }
}
